/**
 * X-MFA-Token del staff de plataforma (2FA de feat/platform-staff-2fa).
 * Vive en memoria y en sessionStorage (se pierde al cerrar la pestana). NUNCA en localStorage, en la URL ni en un log.
 * El backend lo invalida con refresh, re-login o reset: se borra en esos tres casos y al recibir 403 MFA_REQUIRED.
 * Sin dependencias (lo importa apiClient).
 */
const KEY = 'synclyst.platform_mfa';
export const MFA_HEADER = 'X-MFA-Token';
export const MFA_REQUIRED_EVENT = 'platform-mfa:required';
/**
 * 403 generico (sin MFA_REQUIRED) en una ruta de MFA_PATH_RULES. detail: { url }. Hoy /api/monitoring responde 403
 * generico al staff sin 2FA (Backend routers/monitoring.py:25): el Gate montado vuelve a preguntar /mfa/status.
 * No borra el token ni oculta el 403.
 */
export const MFA_FORBIDDEN_EVENT = 'platform-mfa:forbidden';
/** detail: { user } = /me pedido con X-MFA-Token justo despues de verificar (AuthContext lo aplica). */
export const MFA_VERIFIED_EVENT = 'platform-mfa:verified';
export type MfaRequiredDetail = { url?: string };

/**
 * true si `url` (resuelta contra baseURL) es del mismo origen que la pagina o que la base de la API. Misma resolucion
 * que needsMfaHeader. URL invalida => false. Para headers explicitos (p. ej. /me con X-MFA-Token).
 */
export function isOwnApiOrigin(url: string, baseURL?: string, apiBaseURL?: string): boolean {
  try {
    const page = window.location.origin;
    const base = new URL(baseURL || page, page);
    const target = new URL(url, base).origin;
    const allowed = new Set([page]);
    if (apiBaseURL) allowed.add(new URL(apiBaseURL, page).origin);
    return target === base.origin && allowed.has(target);
  } catch {
    return false;
  }
}

/** true si la URL (absoluta o relativa) es /api/internal-communications o una subruta. URL invalida => false. */
export function isInternalCommsUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  try {
    const p = new URL(url, window.location.origin).pathname;
    return p === '/api/internal-communications' || p.startsWith('/api/internal-communications/');
  } catch {
    return false;
  }
}

type Stored = { token: string; expiresAt: number };
let mem: Stored | null = null;
// Generacion de verificacion: sube cada vez que se guarda un token nuevo. Una respuesta de una peticion que empezo
// antes (otra generacion) ya no sirve para decir "falta 2FA" (P3 QA: header tardio tras verificar).
let generation = 0;
export function getMfaGeneration(): number { return generation; }

function read(): Stored | null {
  if (mem) return mem;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Stored>;
    if (typeof v.token === 'string' && typeof v.expiresAt === 'number') mem = { token: v.token, expiresAt: v.expiresAt };
  } catch { /* sin storage */ }
  return mem;
}

export function setPlatformMfaToken(token: string, expiresAt: string | null | undefined): void {
  if (!token) return;
  const ts = expiresAt ? Date.parse(expiresAt) : NaN;
  mem = { token, expiresAt: Number.isFinite(ts) ? ts : Date.now() + 15 * 60_000 };
  generation += 1;
  try { sessionStorage.setItem(KEY, JSON.stringify(mem)); } catch { /* solo memoria */ }
}

/** Token vigente o null (vencido => se borra). */
export function getPlatformMfaToken(): string | null {
  const v = read();
  if (!v) return null;
  if (v.expiresAt <= Date.now()) { clearPlatformMfaToken(); return null; }
  return v.token;
}

export function clearPlatformMfaToken(): void {
  mem = null;
  try { sessionStorage.removeItem(KEY); } catch { /* noop */ }
}

/**
 * Rutas que exigen X-MFA-Token al staff de plataforma. Lista explicita:
 * - `prefix`: solo subrutas (pathname empieza con el prefijo, que termina en '/').
 * - `exact`: el pathname exacto (sin barra final) tambien cuenta.
 * /api/internal-communications: Backend fix/support-thread-access 00bbd424 (Soporte ve y contesta hilos support de
 * todos los tenants solo con platform_staff + 2FA vigente).
 */
const MFA_PATH_RULES: ReadonlyArray<{ prefix?: string; exact?: string }> = [
  { prefix: '/api/platform/' },
  { prefix: '/api/internal-communications/', exact: '/api/internal-communications' },
  // /monitoring (fix/monitoring-mfa-gate): el backend usa require_platform_admin (staff + 2FA vigente).
  { prefix: '/api/monitoring/', exact: '/api/monitoring' },
  { prefix: '/api/admin/logs/', exact: '/api/admin/logs' },
  // Backend 436a0b23: require_platform_admin responde 403 MFA_REQUIRED al staff con 2FA pendiente.
  // Rutas de diagnostico montadas fuera de /api (main.py: /monitoring/test|dashboard|status, /monitoring-status, /test-db).
  { prefix: '/monitoring/', exact: '/monitoring-status' },
  { exact: '/test-db' },
  { exact: '/api/quick-test' },
  { exact: '/api/cors-test' },
  { exact: '/api/background-tasks/status' },
  // Endpoints sueltos de platform-admin dentro de prefijos que COMPARTEN usuarios de tenant: solo el path exacto,
  // para no mandar X-MFA-Token (ni reabrir el Gate) en /api/billing, /api/properties, /api/reports, etc.
  { exact: '/api/auth/test' },
  { exact: '/api/billing/create-one-time-payment' },
  { exact: '/api/employee-invitations/test-auth' },
  { exact: '/api/employee-invitations/test-email' },
  { exact: '/api/employee-invitations/cleanup-orphaned-invitations' },
  { exact: '/api/gifs/cleanup-duplicates' },
  { exact: '/api/properties/admin/daily-ical-sync' },
  { exact: '/api/properties/ical/sync/trigger-manual' },
  { exact: '/api/reports/test' },
  { exact: '/api/emails/send/test' },
  { exact: '/api/time-tracking/test' },
];

function isMfaPath(pathname: string): boolean {
  return MFA_PATH_RULES.some((r) => (r.prefix !== undefined && pathname.startsWith(r.prefix)) || (r.exact !== undefined && pathname === r.exact));
}

/**
 * Rutas que llevan el header: mismo origin que la API, pathname en MFA_PATH_RULES y sin ?ticket= (descargas por
 * ticket no llevan MFA por contrato; los params con ticket los filtra hasTicketParam en apiClient). `baseURL` lo pasa
 * apiClient (config.baseURL o API_BASE_URL) para no importar apiClient aqui (import circular). URL invalida => false.
 */
export function needsMfaHeader(url: string, baseURL?: string): boolean {
  try {
    const base = new URL(baseURL || window.location.origin, window.location.origin);
    const u = new URL(url, base);
    if (u.origin !== base.origin) return false;
    if (!isMfaPath(u.pathname)) return false;
    return !u.searchParams.has('ticket');
  } catch {
    return false;
  }
}
/** true si los params de axios traen ticket (objeto plano o URLSearchParams). */
export function hasTicketParam(params: unknown): boolean {
  if (!params) return false;
  if (params instanceof URLSearchParams) return params.has('ticket');
  if (typeof params === 'object') {
    const v = (params as Record<string, unknown>).ticket;
    return v !== undefined && v !== null;
  }
  return false;
}
